import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Button,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Language,
  Home,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import api from '../../services/api';

interface VerificationResult {
  isValid: boolean;
  card: {
    nome: string;
    cognome: string;
    club: {
      club_name: string;
    };
    year: number;
  };
  dateStr: string;
}

const VerifyCardPage: React.FC = () => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cardId } = useParams<{ cardId: string }>();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<VerificationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Get date_str from query params
  const dateStr = searchParams.get('date_str');

  const verifyCard = useCallback(async () => {
    if (!cardId || !dateStr) {
      setError(t('verify.invalidUrl'));
      setLoading(false);
      return;
    }

    try {
      const response = await api.get(`/verify-card/${cardId}`, {
        params: { date_str: dateStr }
      });
      
      setResult(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('verify.errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [cardId, dateStr, t]);

  useEffect(() => {
    verifyCard();
  }, [verifyCard]);

  const formatDate = (timestamp: string) => {
    const date = new Date(parseInt(timestamp) * 1000);
    const locale = i18n.language === 'it' ? it : enUS;
    return format(date, 'dd/MM/yyyy', { locale });
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'it' ? 'en' : 'it';
    i18n.changeLanguage(newLang);
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Cancel color="error" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h5" color="error" gutterBottom>
            {t('verify.error')}
          </Typography>
          <Typography variant="body1">{error}</Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Button
          startIcon={<Home />}
          onClick={() => navigate('/')}
          variant="outlined"
          size="small"
        >
          Home
        </Button>
        <Button
          startIcon={<Language />}
          onClick={toggleLanguage}
          variant="outlined"
          size="small"
        >
          {i18n.language === 'it' ? 'English' : 'Italiano'}
        </Button>
      </Box>

      <Paper
        elevation={3}
        sx={{
          p: 4,
          textAlign: 'center',
          backgroundColor: result?.isValid ? '#e8f5e9' : '#ffebee',
          border: `2px solid ${result?.isValid ? '#4caf50' : '#f44336'}`,
        }}
      >
        {result?.isValid ? (
          <>
            <CheckCircle sx={{ fontSize: 80, color: '#4caf50', mb: 2 }} />
            <Typography variant="h4" gutterBottom sx={{ color: '#2e7d32' }}>
              {t('verify.validTitle')}
            </Typography>
            <Typography variant="h6" gutterBottom>
              {t('verify.memberInGoodStanding')}
            </Typography>
          </>
        ) : (
          <>
            <Cancel sx={{ fontSize: 80, color: '#f44336', mb: 2 }} />
            <Typography variant="h4" gutterBottom sx={{ color: '#c62828' }}>
              {t('verify.invalidTitle')}
            </Typography>
            <Typography variant="h6" gutterBottom>
              {t('verify.cardNotValid')}
            </Typography>
          </>
        )}

        {result && (
          <Box sx={{ mt: 4, textAlign: 'left', backgroundColor: 'rgba(255,255,255,0.7)', p: 3, borderRadius: 2 }}>
            <Typography variant="body1" gutterBottom>
              <strong>{t('verify.member')}:</strong> {result.card.nome || 'N/A'} {result.card.cognome || ''}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <strong>{t('verify.club')}:</strong> {result.card.club.club_name}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <strong>{t('verify.year')}:</strong> {result.card.year}
            </Typography>
            <Typography variant="body1">
              <strong>{t('verify.issueDate')}:</strong> {formatDate(dateStr!)}
            </Typography>
          </Box>
        )}
      </Paper>

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          {t('verify.securityNote')}
        </Typography>
      </Box>
    </Container>
  );
};

export default VerifyCardPage;